import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { getSelectionKeyboardEvent } from 'vscode/vscode/vs/platform/list/browser/listService';
import { IViewsService } from 'vscode/vscode/vs/workbench/services/views/common/viewsService.service';
import { searchRemoveIcon, searchReplaceIcon } from './searchIcons.js';
import { SearchContext } from 'vscode/vscode/vs/workbench/contrib/search/common/constants';
import { IReplaceService } from 'vscode/vscode/vs/workbench/contrib/search/browser/replace.service';
import { SearchResult, arrayContainsElementOrParent, Match, MatchInNotebook, FileMatch, FolderMatch } from 'vscode/vscode/vs/workbench/contrib/search/browser/searchModel';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { IUriIdentityService } from 'vscode/vscode/vs/platform/uriIdentity/common/uriIdentity.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { registerAction2, Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { category, getSearchView, getElementsToOperateOn, shouldRefocus } from 'vscode/vscode/vs/workbench/contrib/search/browser/searchActionsBase';
import { equals } from 'vscode/vscode/vs/base/common/arrays';

const _moduleId = "vs/workbench/contrib/search/browser/searchActionsRemoveReplace";
registerAction2(class RemoveAction extends Action2 {
    constructor() {
        super({
            id: "search.action.remove" ,
            title: ( localize2WithPath(_moduleId, 0, "Dismiss")),
            category,
            icon: searchRemoveIcon,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(SearchContext.SearchViewVisibleKey, SearchContext.FileMatchOrMatchFocusKey))),
                primary: 20 ,
                mac: {
                    primary: 2048  | 1 ,
                },
            },
            menu: [
                {
                    id: MenuId.SearchContext,
                    group: 'search',
                    order: 2,
                },
                {
                    id: MenuId.SearchActionMenu,
                    group: 'inline',
                    order: 2,
                },
            ]
        });
    }
    run(accessor, context) {
        const viewsService = accessor.get(IViewsService);
        const configurationService = accessor.get(IConfigurationService);
        const searchView = getSearchView(viewsService);
        if (!searchView) {
            return;
        }
        let element = context?.element;
        let viewer = context?.viewer;
        if (!viewer) {
            viewer = searchView.getControl();
        }
        if (!element) {
            element = viewer.getFocus()[0] ?? undefined;
        }
        const elementsToRemove = getElementsToOperateOn(viewer, element, configurationService.getValue('search'));
        let focusElement = viewer.getFocus()[0] ?? undefined;
        if (elementsToRemove.length === 0) {
            return;
        }
        if (!focusElement || (focusElement instanceof SearchResult)) {
            focusElement = element;
        }
        let nextFocusElement;
        const shouldRefocusMatch = shouldRefocus(elementsToRemove, focusElement);
        if (focusElement && shouldRefocusMatch) {
            nextFocusElement = getElementToFocusAfterRemoved(viewer, focusElement, elementsToRemove);
        }
        const searchResult = searchView.searchResult;
        if (searchResult) {
            searchResult.batchRemove(elementsToRemove);
        }
        if (focusElement && shouldRefocusMatch) {
            if (!nextFocusElement) {
                nextFocusElement = getLastNodeFromSameType(viewer, focusElement);
            }
            if (nextFocusElement && !arrayContainsElementOrParent(nextFocusElement, elementsToRemove)) {
                viewer.reveal(nextFocusElement);
                viewer.setFocus([nextFocusElement], getSelectionKeyboardEvent());
                viewer.setSelection([nextFocusElement], getSelectionKeyboardEvent());
            }
        }
        else if (!equals(viewer.getFocus(), viewer.getSelection())) {
            viewer.setSelection(viewer.getFocus());
        }
        viewer.domFocus();
        return;
    }
});
registerAction2(class ReplaceAction extends Action2 {
    constructor() {
        super({
            id: "search.action.replace" ,
            title: ( localize2WithPath(_moduleId, 1, "Replace")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(
                    SearchContext.SearchViewVisibleKey,
                    SearchContext.ReplaceActiveKey,
                    SearchContext.MatchFocusKey,
                    SearchContext.IsEditableItemKey
                ))),
                primary: 1024  | 2048  | 22 ,
            },
            icon: searchReplaceIcon,
            menu: [
                {
                    id: MenuId.SearchContext,
                    when: ( (ContextKeyExpr.and(
                        SearchContext.ReplaceActiveKey,
                        SearchContext.MatchFocusKey,
                        SearchContext.IsEditableItemKey
                    ))),
                    group: 'search',
                    order: 1
                },
                {
                    id: MenuId.SearchActionMenu,
                    when: ( (ContextKeyExpr.and(
                        SearchContext.ReplaceActiveKey,
                        SearchContext.MatchFocusKey,
                        SearchContext.IsEditableItemKey
                    ))),
                    group: 'inline',
                    order: 1
                }
            ]
        });
    }
    async run(accessor, context) {
        return performReplace(accessor, context);
    }
});
registerAction2(class ReplaceAllAction extends Action2 {
    constructor() {
        super({
            id: "search.action.replaceAllInFile" ,
            title: ( localize2WithPath(_moduleId, 2, "Replace All")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(
                    SearchContext.SearchViewVisibleKey,
                    SearchContext.ReplaceActiveKey,
                    SearchContext.FileFocusKey,
                    SearchContext.IsEditableItemKey
                ))),
                primary: 1024  | 2048  | 22 ,
                secondary: [2048  | 1024  | 3 ],
            },
            icon: searchReplaceIcon,
            menu: [
                {
                    id: MenuId.SearchContext,
                    when: ( (ContextKeyExpr.and(
                        SearchContext.ReplaceActiveKey,
                        SearchContext.FileFocusKey,
                        SearchContext.IsEditableItemKey
                    ))),
                    group: 'search',
                    order: 1
                },
                {
                    id: MenuId.SearchActionMenu,
                    when: ( (ContextKeyExpr.and(
                        SearchContext.ReplaceActiveKey,
                        SearchContext.FileFocusKey,
                        SearchContext.IsEditableItemKey
                    ))),
                    group: 'inline',
                    order: 1
                }
            ]
        });
    }
    async run(accessor, context) {
        return performReplace(accessor, context);
    }
});
registerAction2(class ReplaceAllInFolderAction extends Action2 {
    constructor() {
        super({
            id: "search.action.replaceAllInFolder" ,
            title: ( localize2WithPath(_moduleId, 2, "Replace All")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(
                    SearchContext.SearchViewVisibleKey,
                    SearchContext.ReplaceActiveKey,
                    SearchContext.FolderFocusKey,
                    SearchContext.IsEditableItemKey
                ))),
                primary: 1024  | 2048  | 22 ,
                secondary: [2048  | 1024  | 3 ],
            },
            icon: searchReplaceIcon,
            menu: [
                {
                    id: MenuId.SearchContext,
                    when: ( (ContextKeyExpr.and(
                        SearchContext.ReplaceActiveKey,
                        SearchContext.FolderFocusKey,
                        SearchContext.IsEditableItemKey
                    ))),
                    group: 'search',
                    order: 1
                },
                {
                    id: MenuId.SearchActionMenu,
                    when: ( (ContextKeyExpr.and(
                        SearchContext.ReplaceActiveKey,
                        SearchContext.FolderFocusKey,
                        SearchContext.IsEditableItemKey
                    ))),
                    group: 'inline',
                    order: 1
                }
            ]
        });
    }
    async run(accessor, context) {
        return performReplace(accessor, context);
    }
});
function performReplace(accessor, context) {
    const configurationService = accessor.get(IConfigurationService);
    const viewsService = accessor.get(IViewsService);
    const viewlet = getSearchView(viewsService);
    const viewer = context?.viewer ?? viewlet?.getControl();
    if (!viewer) {
        return;
    }
    const element = context?.element ?? viewer.getFocus()[0];
    const elementsToReplace = getElementsToOperateOn(viewer, element ?? undefined, configurationService.getValue('search'));
    let focusElement = viewer.getFocus()[0];
    if (!focusElement || (focusElement && !arrayContainsElementOrParent(focusElement, elementsToReplace)) || (focusElement instanceof SearchResult)) {
        focusElement = element;
    }
    if (elementsToReplace.length === 0) {
        return;
    }
    let nextFocusElement;
    if (focusElement) {
        nextFocusElement = getElementToFocusAfterRemoved(viewer, focusElement, elementsToReplace);
    }
    const searchResult = viewlet?.searchResult;
    if (searchResult) {
        searchResult.batchReplace(elementsToReplace);
    }
    if (focusElement) {
        if (!nextFocusElement) {
            nextFocusElement = getLastNodeFromSameType(viewer, focusElement);
        }
        if (nextFocusElement) {
            viewer.reveal(nextFocusElement);
            viewer.setFocus([nextFocusElement], getSelectionKeyboardEvent());
            viewer.setSelection([nextFocusElement], getSelectionKeyboardEvent());
            if (nextFocusElement instanceof Match) {
                const useReplacePreview = configurationService.getValue().search.useReplacePreview;
                if (!useReplacePreview || hasToOpenFile(accessor, nextFocusElement) || nextFocusElement instanceof MatchInNotebook) {
                    viewlet?.open(nextFocusElement, true);
                }
                else {
                    accessor.get(IReplaceService).openReplacePreview(nextFocusElement, true);
                }
            }
            else if (nextFocusElement instanceof FileMatch) {
                viewlet?.open(nextFocusElement, true);
            }
        }
    }
    viewer.domFocus();
}
function hasToOpenFile(accessor, currBottomElem) {
    if (!(currBottomElem instanceof Match)) {
        return false;
    }
    const activeEditor = accessor.get(IEditorService).activeEditor;
    const file = activeEditor?.resource;
    if (file) {
        return accessor.get(IUriIdentityService).extUri.isEqual(file, currBottomElem.parent().resource);
    }
    return false;
}
function compareLevels(elem1, elem2) {
    if (elem1 instanceof Match) {
        if (elem2 instanceof Match) {
            return 0;
        }
        else {
            return -1;
        }
    }
    else if (elem1 instanceof FileMatch) {
        if (elem2 instanceof Match) {
            return 1;
        }
        else if (elem2 instanceof FileMatch) {
            return 0;
        }
        else {
            return -1;
        }
    }
    else {
        if (elem2 instanceof FolderMatch) {
            return 0;
        }
        else {
            return 1;
        }
    }
}
function getElementToFocusAfterRemoved(viewer, element, elementsToRemove) {
    const navigator = viewer.navigate(element);
    if (element instanceof FolderMatch) {
        while (!!navigator.next() && (!(navigator.current() instanceof FolderMatch) || arrayContainsElementOrParent(navigator.current(), elementsToRemove))) { }
    }
    else if (element instanceof FileMatch) {
        while (!!navigator.next() && (!(navigator.current() instanceof FileMatch) || arrayContainsElementOrParent(navigator.current(), elementsToRemove))) {
            viewer.expand(navigator.current());
        }
    }
    else {
        while (navigator.next() && (!(navigator.current() instanceof Match) || arrayContainsElementOrParent(navigator.current(), elementsToRemove))) {
            viewer.expand(navigator.current());
        }
    }
    return navigator.current();
}
function getLastNodeFromSameType(viewer, element) {
    let lastElem = viewer.lastVisibleElement ?? null;
    while (lastElem) {
        const compareVal = compareLevels(element, lastElem);
        if (compareVal === -1) {
            viewer.expand(lastElem);
            lastElem = viewer.lastVisibleElement;
        }
        else if (compareVal === 1) {
            lastElem = viewer.getParentElement(lastElem);
        }
        else {
            return lastElem;
        }
    }
    return undefined;
}

export { getElementToFocusAfterRemoved, getLastNodeFromSameType };
